/**
 * Anmelden, Registrieren, Bestätigen, Zurücksetzen, Einladung annehmen.
 *
 * Gemeinsame Regel: Die Oberfläche wiederholt, was der Server sagt, und
 * erfindet keine genaueren Auskünfte. Der Server antwortet bei Registrierung
 * und Passwort-Reset absichtlich unabhängig davon, ob es die Adresse gibt —
 * ein hilfsbereites „Diese Adresse kennen wir nicht" hier würde genau das
 * verraten, was dort verborgen wird.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { api, ApiError, setCsrfToken, type Me } from '../lib/api'
import { pageVariants, springSoft } from '../lib/motion'
import { ME_KEY } from '../lib/session'
import { Banner, Button, Card, Spinner, TextField } from '../ui'
import { Footer } from '../components/Shell'

function AuthLayout({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle?: string
  children: React.ReactNode
}) {
  return (
    <div className="auth">
      <motion.div
        className="auth__panel"
        variants={pageVariants}
        initial="hidden"
        animate="visible"
        transition={springSoft()}
      >
        <div className="auth__head">
          <h1>{title}</h1>
          {subtitle && <p className="muted">{subtitle}</p>}
        </div>
        {children}
        <Footer />
      </motion.div>
    </div>
  )
}

/** Übersetzt einen API-Fehler in etwas, das man einem Menschen zeigen kann. */
function useFormError() {
  const [error, setError] = useState<ApiError | null>(null)
  return {
    error,
    setError,
    fields: error?.fieldErrors() ?? {},
    message: error && Object.keys(error.fieldErrors()).length === 0 ? error.message : null,
    reset: () => setError(null),
  }
}

// ------------------------------------------------------------------- Login --

export function LoginPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const form = useFormError()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const login = useMutation({
    mutationFn: () => api.post<Me>('/api/auth/login', { email, password }),
    onSuccess: (me) => {
      setCsrfToken(me.csrf_token)
      queryClient.setQueryData(ME_KEY, me)
      navigate('/', { replace: true })
    },
    onError: (error) => form.setError(error as ApiError),
  })

  const externalError = OAUTH_ERRORS[params.get('fehler') ?? '']

  return (
    <AuthLayout title="Dienstplan" subtitle="Dienst- und Schichtplanung">
      <Card>
        <form
          className="stack"
          onSubmit={(event: FormEvent) => {
            event.preventDefault()
            form.reset()
            login.mutate()
          }}
        >
          {externalError && <Banner tone="error">{externalError}</Banner>}
          {form.message && <Banner tone="error">{form.message}</Banner>}

          <TextField
            label="E-Mail-Adresse"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            error={form.fields['email']}
          />
          <TextField
            label="Passwort"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            error={form.fields['password']}
          />
          <Button type="submit" block busy={login.isPending}>
            Anmelden
          </Button>
        </form>

        <div className="auth__divider" style={{ margin: 'var(--sp-4) 0' }}>
          oder
        </div>

        <Button
          variant="outlined"
          block
          onClick={() => {
            window.location.href = '/api/auth/google/start'
          }}
        >
          Mit Google anmelden
        </Button>
      </Card>

      <p className="auth__switch">
        <Link to="/passwort-vergessen">Passwort vergessen?</Link>
        {' · '}
        <Link to="/registrieren">Konto anlegen</Link>
      </p>
    </AuthLayout>
  )
}

const OAUTH_ERRORS: Record<string, string> = {
  google_abgebrochen: 'Die Anmeldung bei Google wurde abgebrochen.',
  google_ungueltig: 'Die Anmeldung bei Google ist fehlgeschlagen. Bitte erneut versuchen.',
  konto_gesperrt: 'Dieses Konto ist gesperrt. Bitte wende dich an deinen Betrieb.',
  adresse_belegt:
    'Zu dieser Adresse gibt es bereits ein Konto mit Passwort. Melde dich damit an und ' +
    'verknüpfe Google anschließend im Profil.',
}

// ----------------------------------------------------------- Registrierung --

export function RegisterPage() {
  const form = useFormError()
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const register = useMutation({
    mutationFn: () =>
      api.post<{ message: string }>('/api/auth/register', {
        email,
        password,
        full_name: fullName,
      }),
    onError: (error) => form.setError(error as ApiError),
  })

  if (register.isSuccess) {
    return (
      <AuthLayout title="Fast geschafft">
        <Card>
          <Banner tone="success">{register.data.message}</Banner>
          <p className="muted" style={{ marginTop: 'var(--sp-4)' }}>
            Schau in dein Postfach und bestätige die Adresse. Danach kannst du dich anmelden.
          </p>
        </Card>
        <p className="auth__switch">
          <Link to="/anmelden">Zur Anmeldung</Link>
        </p>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Konto anlegen">
      <Card>
        <form
          className="stack"
          onSubmit={(event: FormEvent) => {
            event.preventDefault()
            form.reset()
            register.mutate()
          }}
        >
          {form.message && <Banner tone="error">{form.message}</Banner>}
          <TextField
            label="Name"
            autoComplete="name"
            required
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            error={form.fields['full_name']}
          />
          <TextField
            label="E-Mail-Adresse"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            error={form.fields['email']}
          />
          <TextField
            label="Passwort"
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            hint="Mindestens 10 Zeichen. Länge hilft mehr als Sonderzeichen."
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            error={form.fields['password']}
          />
          <Button type="submit" block busy={register.isPending}>
            Konto anlegen
          </Button>
        </form>
      </Card>
      <p className="auth__switch">
        Schon ein Konto? <Link to="/anmelden">Anmelden</Link>
      </p>
    </AuthLayout>
  )
}

// -------------------------------------------------------------- Bestätigen --

export function VerifyEmailPage() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const verify = useQuery({
    queryKey: ['verify', token],
    queryFn: () => api.post<{ message: string }>('/api/auth/verify-email', { token }),
    enabled: token.length > 0,
    retry: false,
  })

  return (
    <AuthLayout title="E-Mail bestätigen">
      <Card>
        {!token && <Banner tone="error">Dieser Link ist unvollständig.</Banner>}
        {verify.isPending && token && <Spinner label="Wird geprüft …" />}
        {verify.isError && (
          <Banner tone="error">{(verify.error as ApiError).message}</Banner>
        )}
        {verify.isSuccess && <Banner tone="success">{verify.data.message}</Banner>}
      </Card>
      <p className="auth__switch">
        <Link to="/anmelden">Zur Anmeldung</Link>
      </p>
    </AuthLayout>
  )
}

/**
 * Bestätigt eine neue Anmeldeadresse.
 *
 * Setzt eine Anmeldung voraus: Der Link soll in derselben Sitzung geöffnet
 * werden, in der der Wechsel angestoßen wurde. Wer abgemeldet ist, sieht
 * deshalb keinen Fehler, sondern den Hinweis, sich zuerst anzumelden — mit der
 * bisherigen Adresse, denn die gilt bis zur Bestätigung weiter.
 */
export function ConfirmEmailChangePage() {
  const [params] = useSearchParams()
  const queryClient = useQueryClient()
  const token = params.get('token') ?? ''

  const confirm = useQuery({
    queryKey: ['email-change-confirm', token],
    queryFn: async () => {
      const updated = await api.post<Me>('/api/me/email/confirm', { token })
      // Kopfzeile und Profil zeigen die Adresse — ohne das stünde dort bis zum
      // nächsten Laden noch die alte.
      queryClient.setQueryData(['me'], updated)
      return updated
    },
    enabled: token.length > 0,
    retry: false,
  })

  const fehler = confirm.error as ApiError | undefined
  const nichtAngemeldet = fehler?.status === 401

  return (
    <AuthLayout title="Neue E-Mail-Adresse">
      <Card>
        {!token && <Banner tone="error">Dieser Link ist unvollständig.</Banner>}
        {confirm.isPending && token && <Spinner label="Wird geprüft …" />}
        {nichtAngemeldet && (
          <Banner tone="info">
            Bitte melde dich zuerst an — noch mit deiner bisherigen Adresse — und öffne den Link
            dann erneut.
          </Banner>
        )}
        {confirm.isError && !nichtAngemeldet && <Banner tone="error">{fehler?.message}</Banner>}
        {confirm.isSuccess && (
          <Banner tone="success">
            Erledigt. Du meldest dich ab jetzt mit <strong>{confirm.data.email}</strong> an.
          </Banner>
        )}
      </Card>
      <p className="auth__switch">
        <Link to={confirm.isSuccess ? '/profil' : '/anmelden'}>
          {confirm.isSuccess ? 'Zum Profil' : 'Zur Anmeldung'}
        </Link>
      </p>
    </AuthLayout>
  )
}

// ---------------------------------------------------------------- Passwort --

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const request = useMutation({
    mutationFn: () =>
      api.post<{ message: string }>('/api/auth/password-reset/request', { email }),
  })

  return (
    <AuthLayout
      title="Passwort vergessen"
      subtitle="Wir schicken dir einen Link zum Zurücksetzen."
    >
      <Card>
        {request.isSuccess ? (
          <Banner tone="success">{request.data.message}</Banner>
        ) : (
          <form
            className="stack"
            onSubmit={(event: FormEvent) => {
              event.preventDefault()
              request.mutate()
            }}
          >
            <TextField
              label="E-Mail-Adresse"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <Button type="submit" block busy={request.isPending}>
              Link anfordern
            </Button>
          </form>
        )}
      </Card>
      <p className="auth__switch">
        <Link to="/anmelden">Zurück zur Anmeldung</Link>
      </p>
    </AuthLayout>
  )
}

export function ResetPasswordPage() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const form = useFormError()
  const [password, setPassword] = useState('')

  const reset = useMutation({
    mutationFn: () =>
      api.post<{ message: string }>('/api/auth/password-reset/confirm', {
        token,
        new_password: password,
      }),
    onError: (error) => form.setError(error as ApiError),
  })

  return (
    <AuthLayout title="Neues Passwort">
      <Card>
        {reset.isSuccess ? (
          <Banner tone="success">{reset.data.message}</Banner>
        ) : (
          <form
            className="stack"
            onSubmit={(event: FormEvent) => {
              event.preventDefault()
              form.reset()
              reset.mutate()
            }}
          >
            {form.message && <Banner tone="error">{form.message}</Banner>}
            {!token && <Banner tone="error">Dieser Link ist unvollständig.</Banner>}
            <TextField
              label="Neues Passwort"
              type="password"
              autoComplete="new-password"
              required
              minLength={10}
              hint="Mindestens 10 Zeichen."
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <Button type="submit" block busy={reset.isPending} disabled={!token}>
              Passwort setzen
            </Button>
          </form>
        )}
      </Card>
      <p className="auth__switch">
        <Link to="/anmelden">Zur Anmeldung</Link>
      </p>
    </AuthLayout>
  )
}

// --------------------------------------------------------------- Einladung --

type InvitationPreview = {
  tenant_name: string
  email: string
  role: string
  role_label: string
}

export function InvitationPage({ me }: { me: Me | null }) {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const preview = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => api.get<InvitationPreview>(`/api/auth/invitation?token=${token}`),
    enabled: token.length > 0,
    retry: false,
  })

  const accept = useMutation({
    mutationFn: () => api.post<Me>('/api/auth/invitation/accept', { token }),
    onSuccess: (updated) => {
      setCsrfToken(updated.csrf_token)
      queryClient.setQueryData(ME_KEY, updated)
      navigate('/', { replace: true })
    },
  })

  return (
    <AuthLayout title="Einladung">
      <Card>
        {!token && <Banner tone="error">Dieser Link ist unvollständig.</Banner>}
        {preview.isPending && token && <Spinner label="Einladung wird geprüft …" />}
        {preview.isError && <Banner tone="error">{(preview.error as ApiError).message}</Banner>}

        {preview.isSuccess && (
          <div className="stack">
            <p>
              <strong>{preview.data.tenant_name}</strong> lädt dich als{' '}
              {preview.data.role_label} ein.
            </p>
            <p className="muted">Die Einladung gilt für {preview.data.email}.</p>

            {!me && (
              <Banner tone="info">
                Melde dich mit {preview.data.email} an, um die Einladung anzunehmen.
              </Banner>
            )}
            {me && me.email !== preview.data.email && (
              <Banner tone="warning">
                Du bist als {me.email} angemeldet. Diese Einladung gilt für{' '}
                {preview.data.email}.
              </Banner>
            )}
            {accept.isError && (
              <Banner tone="error">{(accept.error as ApiError).message}</Banner>
            )}

            {me ? (
              <Button
                block
                busy={accept.isPending}
                disabled={me.email !== preview.data.email}
                onClick={() => accept.mutate()}
              >
                Einladung annehmen
              </Button>
            ) : (
              <Button block onClick={() => navigate(`/anmelden`)}>
                Zur Anmeldung
              </Button>
            )}
          </div>
        )}
      </Card>
    </AuthLayout>
  )
}
